import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { zodResolver } from "@hookform/resolvers/zod";
import { useNotification } from "hooks/useNotification";
import {
  createTransportSedel,
  TTransportSedelSchema,
  transportSedelSchema,
  UppdragQueryKey,
} from "api/uppdrag.api";
import { Uppdrag, UppdragStatus } from "types/uppdrag.type";
import { useWasteStationList } from "api/wasteStation.api";
import { useVehicles } from "api/vehicle.api";
import { vehicleTypeToName } from "types/vehicle.type";
import { TransportSedelQueryKey } from "api/transportSedel.api";

export const useCreateTransportSedelLogic = (
  uppdrag: Uppdrag,
  onSuccessCb?: () => void,
) => {
  const queryClient = useQueryClient();
  const notification = useNotification();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const form = useForm<TTransportSedelSchema>({
    resolver: zodResolver(transportSedelSchema),
    defaultValues: {
      timeOfDepartureUtc:
        uppdrag.status === UppdragStatus.Scheduled ? undefined : new Date(),
      vehicleUuid: uppdrag.assignedToUser?.primaryVehicle?.uuid,
    },
  });

  useEffect(() => {
    if (isModalOpen) {
      form.reset({
        timeOfDepartureUtc:
          uppdrag.status === UppdragStatus.Scheduled ? undefined : new Date(),
        vehicleUuid: uppdrag.assignedToUser?.primaryVehicle?.uuid,
      });
    }
  }, [form, isModalOpen, uppdrag]);

  const onModalClose = () => {
    setIsModalOpen(false);
  };

  const afterModalClose = () => {
    form.reset();
  };

  const createMutation = useMutation({
    ...createTransportSedel(uppdrag.id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [UppdragQueryKey.GetOne],
      });
      queryClient.invalidateQueries({
        queryKey: [TransportSedelQueryKey.GetList],
      });
      if (onSuccessCb) {
        onSuccessCb();
      }

      onModalClose();
    },
    onError: () => notification.error(),
  });

  const onSubmit = form.handleSubmit((data: TTransportSedelSchema) => {
    createMutation.mutate(data);
  });

  // Wastestations
  const { data: wasteStations } = useWasteStationList();
  const wasteStationDropdownItems = useMemo(
    () =>
      (wasteStations ?? [])
        .map((x) => ({ label: x.name, value: x.uuid }))
        .sort((a, b) => (a.label < b.label ? -1 : 1)),
    [wasteStations],
  );

  // Vehicles
  const { data: vehicles } = useVehicles();
  const vehicleDropdownItems = useMemo(
    () =>
      (vehicles ?? [])
        .map(({ uuid, registrationNumber, type }) => ({
          label: `${registrationNumber} - ${vehicleTypeToName(type)}`,
          value: uuid,
        }))
        .sort((a, b) => (a.label < b.label ? -1 : 1)),
    [vehicles],
  );

  return {
    isModalOpen,
    openModal: () => setIsModalOpen(true),
    form,
    onSubmit,
    mutation: createMutation,
    onModalClose,
    afterModalClose,
    uppdrag,
    wasteStationDropdownItems,
    vehicleDropdownItems,
  };
};

export type CreateTransportSedelLogicRetVal = ReturnType<
  typeof useCreateTransportSedelLogic
>;
